import React from 'react';
import type { Sensor3P } from '../ScopingEditor/useGetState';
import { type Dt3pDeviceStatus, DT3P_STATUS_COLORS } from '../ScopingEditor/dt3pStatus';
import {
  type LiveOccupancyStatus,
  LIVE_OCC_COLORS,
  SENSOR3P_LIVE_FILTER_ID,
} from '../ScopingEditor/liveOccupancy';
import { SVGProps, memo } from 'react';
import { AutoTooltip } from '@/components';
import { ICONS_SPRITE_URL } from '@/components/primitives/icon/Icon';
import { useZoom } from '../ZoomContext';
import { useCanvasColors } from './canvasColors';
import { TOOLTIP_CONTENT_WIDTH } from '@/pages/LivePage/shared/sensorUtils';

export const SENSOR3P_ICON_SIZE = 1.5;
/** Icon size for a sensor3p that is snapped to a spot — small enough to sit inside SPOT_ICON_SIZE. */
export const SENSOR3P_SNAPPED_ICON_SIZE = 1.0;

// Fixed IDs for the single shared set of sensor3p filters.
// All Sensor3pItem instances reference these instead of creating per-instance <defs>.
const SENSOR3P_FILTER_NORMAL_ID = 'sensor3p-filter-normal';
const SENSOR3P_FILTER_SELECTED_ID = 'sensor3p-filter-selected';
const SENSOR3P_FILTER_SNAPPED_ID = 'sensor3p-filter-snapped';
const SENSOR3P_FILTER_STATUS_PLACEHOLDER_ID = 'sensor3p-filter-status-placeholder';
const SENSOR3P_FILTER_STATUS_WARNING_ID = 'sensor3p-filter-status-warning';
const SENSOR3P_FILTER_STATUS_CONNECTED_ID = 'sensor3p-filter-status-connected';
const SENSOR3P_FILTER_STATUS_ERROR_ID = 'sensor3p-filter-status-error';

const STATUS_FILTER_ID: Record<Dt3pDeviceStatus, string> = {
  placeholder: SENSOR3P_FILTER_STATUS_PLACEHOLDER_ID,
  warning: SENSOR3P_FILTER_STATUS_WARNING_ID,
  connected: SENSOR3P_FILTER_STATUS_CONNECTED_ID,
  error: SENSOR3P_FILTER_STATUS_ERROR_ID,
};

/**
 * Renders a single shared <defs> block with the sensor3p color filters.
 * Mount this once inside the parent SVG so all Sensor3pItem instances can
 * reference the filters by ID without duplicating them.
 */
export function Sensor3pFilters() {
  const colors = useCanvasColors();
  return (
    <defs>
      {/* Normal: recolor icon to zone idle gray (fallback, superseded by status filters) */}
      <filter id={SENSOR3P_FILTER_NORMAL_ID}>
        <feFlood floodColor={colors.roomStrokeIdle} result="color" />
        <feComposite in="color" in2="SourceAlpha" operator="in" />
      </filter>
      {/* Selected: recolor icon using theme-aware stroke color */}
      <filter id={SENSOR3P_FILTER_SELECTED_ID}>
        <feFlood floodColor={colors.roomStroke} result="color" />
        <feComposite in="color" in2="SourceAlpha" operator="in" />
      </filter>
      {/* Snapped: white icon to contrast against the filled spot */}
      <filter id={SENSOR3P_FILTER_SNAPPED_ID}>
        <feFlood floodColor="white" result="color" />
        <feComposite in="color" in2="SourceAlpha" operator="in" />
      </filter>
      {/* Status: placeholder — not yet configured */}
      <filter id={SENSOR3P_FILTER_STATUS_PLACEHOLDER_ID}>
        <feFlood floodColor={DT3P_STATUS_COLORS.placeholder} result="color" />
        <feComposite in="color" in2="SourceAlpha" operator="in" />
      </filter>
      {/* Status: warning — external_device_id set but not yet provisioned */}
      <filter id={SENSOR3P_FILTER_STATUS_WARNING_ID}>
        <feFlood floodColor={DT3P_STATUS_COLORS.warning} result="color" />
        <feComposite in="color" in2="SourceAlpha" operator="in" />
      </filter>
      {/* Status: connected — provisioned and heartbeating */}
      <filter id={SENSOR3P_FILTER_STATUS_CONNECTED_ID}>
        <feFlood floodColor={DT3P_STATUS_COLORS.connected} result="color" />
        <feComposite in="color" in2="SourceAlpha" operator="in" />
      </filter>
      {/* Status: error — provisioned but no heartbeat within 30 min */}
      <filter id={SENSOR3P_FILTER_STATUS_ERROR_ID}>
        <feFlood floodColor={DT3P_STATUS_COLORS.error} result="color" />
        <feComposite in="color" in2="SourceAlpha" operator="in" />
      </filter>
      {/* Live occupancy: available — sensor3p reporting no occupancy in the last window */}
      <filter id={SENSOR3P_LIVE_FILTER_ID.available}>
        <feFlood floodColor={LIVE_OCC_COLORS.available} result="color" />
        <feComposite in="color" in2="SourceAlpha" operator="in" />
      </filter>
      {/* Live occupancy: occupied — sensor3p reporting occupancy > 0 */}
      <filter id={SENSOR3P_LIVE_FILTER_ID.occupied}>
        <feFlood floodColor={LIVE_OCC_COLORS.occupied} result="color" />
        <feComposite in="color" in2="SourceAlpha" operator="in" />
      </filter>
      {/* Live occupancy: unknown — no data yet or sensor not linked */}
      <filter id={SENSOR3P_LIVE_FILTER_ID.unknown}>
        <feFlood floodColor={LIVE_OCC_COLORS.unknown} result="color" />
        <feComposite in="color" in2="SourceAlpha" operator="in" />
      </filter>
    </defs>
  );
}

function Sensor3pItem({
  x = 0,
  y = 0,
  name,
  id,
  device_type,
  status = 'placeholder',
  liveStatus,
  historicalColor,
  tooltipStatus,
  tooltipStatusContent,
  setupMode = false,
  selected,
  selecting = false,
  fade = false,
  locked = false,
  isSnapped = false,
  onClick,
  ...rest
}: {
  selected: boolean;
  device_type: Sensor3P['device_type'];
  status?: Dt3pDeviceStatus;
  /** Live occupancy status — when set, overrides `status` for color. Used in the live floor plan view. */
  liveStatus?: LiveOccupancyStatus;
  /** Historical utilization color — when set, renders a gradient circle badge. Used in the historical floor plan view. */
  historicalColor?: string;
  /** Tooltip status row — mirrors the room/zone ItemRenderer pattern. */
  tooltipStatus?: { label: string; value: string };
  /** Rich tooltip content — when set, overrides tooltipStatus. */
  tooltipStatusContent?: React.ReactNode;
  /** When true, renders the icon in the theme-aware dark color with no status/occupancy coding. */
  setupMode?: boolean;
  selecting?: boolean;
  fade?: boolean;
  locked?: boolean;
  isSnapped?: boolean;
  id: string;
} & SVGProps<SVGGElement> &
  Pick<Sensor3P, 'name'> & { x?: number; y?: number }) {
  const { adjustedZoom } = useZoom();
  const size = SENSOR3P_ICON_SIZE / adjustedZoom;
  const snappedSize = SENSOR3P_SNAPPED_ICON_SIZE / adjustedZoom;
  const showAsSelected = selected || selecting;
  const iconName =
    device_type === 'motion_sensor'
      ? 'butlrmotionoutlined'
      : device_type === 'desk_sensor'
      ? 'icon-desksensoroutlined'
      : liveStatus === 'occupied'
      ? 'useroutlined'
      : 'icon-spotoutlined';
  // Motion sensors in live mode show as a colored circle badge with a white icon.
  const showCircleBadge =
    device_type === 'motion_sensor' && !!liveStatus && !isSnapped && !showAsSelected && !setupMode;
  // Historical mode: all sensors with utilization data show as a colored circle badge.
  const showHistoricalCircleBadge =
    !!historicalColor && !isSnapped && !showAsSelected && !setupMode;
  // In setup mode, all sensors show as a white circle badge with a black icon (mirrors Room Entrance).
  const showSetupBadge = setupMode && !isSnapped && !showAsSelected;
  // Use snappedSize for badges so the overall circle stays comparable to the spot icon.
  const displaySize =
    isSnapped || showCircleBadge || showHistoricalCircleBadge || showSetupBadge
      ? snappedSize
      : size;
  const circleRadius = snappedSize * 0.75;
  const filterId = isSnapped
    ? SENSOR3P_FILTER_SNAPPED_ID
    : showAsSelected || showSetupBadge
    ? SENSOR3P_FILTER_SELECTED_ID
    : showCircleBadge
    ? SENSOR3P_FILTER_SNAPPED_ID // white icon on colored circle
    : showHistoricalCircleBadge
    ? SENSOR3P_FILTER_SNAPPED_ID // white icon on historical colored circle
    : liveStatus
    ? SENSOR3P_LIVE_FILTER_ID[liveStatus]
    : STATUS_FILTER_ID[status];

  return (
    <g
      data-id="sensor3p-item"
      id={id}
      onClick={onClick}
      transform={`translate(${x}, ${y})`}
      style={{ pointerEvents: locked ? 'none' : 'all', opacity: fade ? 0.5 : 1 }}
      data-selected={selected}
      {...rest}
    >
      <AutoTooltip
        zIndex={100}
        maxWidth={260}
        label={
          <div className="flex flex-col gap-2" style={{ width: TOOLTIP_CONTENT_WIDTH }}>
            <h3 className="font-semibold">{name}</h3>
            {tooltipStatusContent}
            {!tooltipStatusContent && tooltipStatus && (
              <div className="flex flex-row justify-between">
                <span className="mr-2">{tooltipStatus.label}</span>
                <span>{tooltipStatus.value}</span>
              </div>
            )}
          </div>
        }
        variant="white"
        placement="top"
        keepOpenOnMouseOver
        forceOpen={selected}
        autoUpdateOnAnimationFrame
      >
        {(props) => (
          <g transform="scale(1, -1)" {...props}>
            {showCircleBadge && (
              <circle
                cx={0}
                cy={0}
                r={circleRadius}
                fill={LIVE_OCC_COLORS[liveStatus!]}
                pointerEvents="none"
              />
            )}
            {showHistoricalCircleBadge && (
              <circle cx={0} cy={0} r={circleRadius} fill={historicalColor!} pointerEvents="none" />
            )}
            {showSetupBadge && (
              <circle cx={0} cy={0} r={circleRadius} fill="white" pointerEvents="none" />
            )}
            {/* pointer-events:none so the <use> bounding box doesn't expand the hit area */}
            <use
              href={`${ICONS_SPRITE_URL}#icon-${iconName}`}
              width={displaySize}
              height={displaySize}
              x={-displaySize / 2}
              y={-displaySize / 2}
              filter={`url(#${filterId})`}
              pointerEvents="none"
            />
            {/* sole hit target: sized to the visible circle badge or icon */}
            <circle
              cx={0}
              cy={0}
              r={
                showCircleBadge || showHistoricalCircleBadge || showSetupBadge
                  ? circleRadius
                  : size * 0.4
              }
              fill="transparent"
              pointerEvents="all"
            />
          </g>
        )}
      </AutoTooltip>
    </g>
  );
}

export default memo(Sensor3pItem);
